import { civilFromParts, compareCivil } from '../../shared/date/civil.js'
import type { CivilDate } from '../../shared/date/civil.js'
import { addMonths, referenceMonthOf } from '../../shared/date/reference-month.js'
import type { ReferenceMonth } from '../../shared/date/reference-month.js'
import type { CreditCard } from './credit-card.js'

/**
 * O ciclo a que uma compra pertence: qual fatura, quando ela fecha e quando
 * vence.
 *
 * **Não dá `settlementDate` à compra.** Compra no cartão é `deferred`, e
 * `deferred` → `settlementDate` é sempre `null`: quem carrega o dinheiro é a
 * `Invoice`, e quem liquida é a transação de pagamento, que é `settlement`.
 * O que este ciclo preenche é `Invoice.closingDate`, `Invoice.dueDate` e o
 * `invoiceId` da compra. Escrever `settlementDate = dueDate` aqui é a contagem
 * dupla que a ADR-0004 existe para impedir — o caixa somaria a compra e o
 * pagamento da fatura. Ver `Docs/03-Arquitetura/Competência e caixa.md`.
 *
 * `referenceMonth` é o mês do **fechamento**, não o da compra nem o do
 * vencimento: uma compra em 26/07 num cartão que fecha dia 25 é da fatura
 * `2026-08`, que vence em setembro. É esse mês que entra no `Invoice.id`
 * (`${creditCardId}_${referenceMonth}`).
 */
export type InvoiceCycle = {
  /** `'2026-08'` — o mês do fechamento. */
  referenceMonth: ReferenceMonth
  closingDate: CivilDate
  dueDate: CivilDate
}

const MIN_CYCLE_DAY = 1
const MAX_CYCLE_DAY = 31

/**
 * `closingDay` e `dueDay` são validados **aqui**, e não deixados por conta da
 * saturação de `civilFromParts`.
 *
 * A saturação não tem limite superior: `civilFromParts(2026, 2, 1e15)` devolve
 * `'2026-02-28'` calado, e `Number.MAX_SAFE_INTEGER` também. Ela não distingue
 * "fechamento 31 em fevereiro" — que é o comportamento pretendido, e tem teste
 * — de "dia 32 vindo de um off-by-one", que é bug. Quem sabe que a faixa é
 * 1..31 é o cartão, então a cobrança é na fronteira do cartão.
 *
 * `Number.isInteger` já recusa `NaN` e infinito.
 *
 * **Exportada desde a Task 8**, porque apareceu o outro chamador que
 * `Docs/02-Dominio/CreditCard.md` sempre nomeou: *"quem cobra é a validação de
 * quem escreve"*. Quem escreve é `adapters/firebase/credit-card-repository.ts`,
 * e sem isto um cartão com `closingDay: 40` entra no banco calado e só explode
 * depois, na primeira fatura que alguém tentar montar — longe do formulário que
 * o criou. Repetir a faixa lá seria dois lugares para errá-la.
 */
export function assertCycleDay(day: number, field: 'closingDay' | 'dueDay'): void {
  if (!Number.isInteger(day) || day < MIN_CYCLE_DAY || day > MAX_CYCLE_DAY) {
    throw new Error(
      `ciclo de fatura: ${field} precisa ser inteiro de ${String(MIN_CYCLE_DAY)} a ${String(MAX_CYCLE_DAY)}, recebi ${String(day)}`,
    )
  }
}

/**
 * O dia `day` dentro do mês de referência, saturado no último dia do mês pelo
 * `civilFromParts` — dia 31 em fevereiro vira 28, ou 29 em ano bissexto.
 *
 * As fatias são seguras porque todo `ReferenceMonth` que chega aqui vem de
 * `referenceMonthOf` ou de `addMonths`, e os dois validam a forma `YYYY-MM`.
 */
function dateInMonth(month: ReferenceMonth, day: number): CivilDate {
  return civilFromParts(Number(month.slice(0, 4)), Number(month.slice(5, 7)), day)
}

/**
 * A qual fatura pertence uma compra na data `date`.
 *
 * A regra, de `Docs/02-Dominio/CreditCard.md`: a compra pertence à fatura que
 * **fecha no primeiro `closingDay` igual ou posterior a `date`**. Igual: compra
 * no próprio dia do fechamento ainda é dessa fatura.
 *
 * O vencimento é a **primeira ocorrência de `dueDay` depois do fechamento**, e
 * a comparação é entre os dias **nominais** do cartão, não entre as datas já
 * saturadas:
 *
 * - `dueDay > closingDay` → vence no mesmo mês (fecha 05/07, vence 20/07);
 * - `dueDay <= closingDay` → vence no mês seguinte (fecha 25/07, vence 10/08).
 *
 * Errar esse par desloca a visão de caixa de todo cartão em um mês inteiro, e
 * nada reclama — uma implementação que sempre soma um mês passa no primeiro
 * caso e falha no segundo.
 *
 * Por que **nominal**: um cartão que fecha 31 e vence 28 vence em 28/03 para a
 * fatura que fecha em 28/02, e não no mesmo dia em que fechou. Comparar as
 * datas saturadas juntaria as duas em fevereiro. O empate (`dueDay ===
 * closingDay`) cai no mês seguinte pelo mesmo motivo: prazo zero não é ciclo de
 * cartão, é o mesmo dia.
 *
 * A saturação nunca inverte a ordem: no mesmo mês, `min(dueDay, último) >=
 * min(closingDay, último)` sempre que `dueDay > closingDay` — no máximo empata,
 * quando os dois passam do último dia do mês. Há varredura para isso.
 */
export function invoiceCycleFor(
  date: CivilDate,
  card: Pick<CreditCard, 'closingDay' | 'dueDay'>,
): InvoiceCycle {
  const { closingDay, dueDay } = card
  assertCycleDay(closingDay, 'closingDay')
  assertCycleDay(dueDay, 'dueDay')

  // `referenceMonthOf` valida a data civil, então '2026-02-30' para aqui.
  const purchaseMonth = referenceMonthOf(date)
  const closesInPurchaseMonth = compareCivil(dateInMonth(purchaseMonth, closingDay), date) >= 0
  const referenceMonth = closesInPurchaseMonth ? purchaseMonth : addMonths(purchaseMonth, 1)
  const dueMonth = dueDay > closingDay ? referenceMonth : addMonths(referenceMonth, 1)

  return {
    referenceMonth,
    closingDate: dateInMonth(referenceMonth, closingDay),
    dueDate: dateInMonth(dueMonth, dueDay),
  }
}
